import { useEffect, useRef, useState, type RefObject } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  useFieldArray,
  useForm,
  type FieldErrors,
  type UseFormReturn,
} from "react-hook-form";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Coins,
  IdCard,
  Info,
  Loader2,
  Plug,
  Plus,
  Receipt,
  Save,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { ProgramsApi, PROGRAM_FIELD_FROM_API, programKeys } from "@/api/perkpoint/programs";
import { apiErrorMessage, apiFieldErrors } from "@/api/perkpoint/client";
import {
  BONUS_COMBINE_OPTIONS,
  CONSENT_CHANNEL_OPTIONS,
  DATA_REGION_OPTIONS,
  ENROLMENT_MODE_OPTIONS,
  EVENT_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  LANGUAGE_OPTIONS,
  MEMBER_ID_TYPE_OPTIONS,
  NEGATIVE_BALANCE_OPTIONS,
  PERSONAL_DATA_OPTIONS,
  PROGRAM_TIER_CHOICES,
  ROUNDING_OPTIONS,
  TIME_ZONE_OPTIONS,
  contactRoleOptions,
  type BonusCombineMode,
  type ConsentChannel,
  type DataRegion,
  type EnrolmentMode,
  type Industry,
  type MemberIdType,
  type NegativeBalancePolicy,
  type PersonalDataField,
  type PointsExpiryMode,
  type PointsRounding,
  type ProgramConfig,
  type ProgramEventType,
  type ProgramTimeZone,
  type SupportedLanguage,
} from "./programs/mock-programs";
import {
  CustomAttributesField,
  EnumSelectField,
  HexColorInput,
  MultiCheckField,
  atLeastOne,
  expiryMonthsRules,
  optionalHexColor,
  optionalWholeNumber,
  parseCancellationPeriod,
  parseOptionalWholeNumber,
  parseWholeNumber,
  requiredEmail,
  requiredSlug,
  requiredText,
  requiredWholeNumber,
} from "./programs/config-controls";

type CreateSection =
  | "identity"
  | "region"
  | "economy"
  | "members"
  | "integration"
  | "team";

type ContactValues = {
  name: string;
  email: string;
  role: string;
  countryCode: string;
  phoneNumber: string;
};

type CreateProgramFormValues = {
  name: string;
  code: string;
  clientName: string;
  billingContactName: string;
  billingContactEmail: string;
  vatNumber: string;
  websiteUrl: string;
  programLogoUri: string;
  industry: Industry;
  programOwner: string;
  brandPrimaryColor: string;
  memberTermsUrl: string;
  memberTermsVersion: string;
  privacyPolicyUrl: string;
  privacyPolicyVersion: string;
  loyaltyPointsName: string;
  costPerPoint: string;
  cashbackPercentage: string;
  pointsRounding: PointsRounding;
  pointsHoldDays: string;
  cancellationPeriodDays: string;
  pointsExpiryMode: PointsExpiryMode;
  pointsExpiryMonths: string;
  negativeBalancePolicy: NegativeBalancePolicy;
  bonusCombineMode: BonusCombineMode;
  paymentCurrency: string;
  invoiceFrequency: string;
  offerDenomination: string;
  txnCommissionPct: string;
  timeZone: ProgramTimeZone;
  dataRegion: DataRegion;
  supportedLanguages: SupportedLanguage[];
  address: string;
  country: string;
  city: string;
  startingTier: string;
  contacts: ContactValues[];
  enrolmentMode: EnrolmentMode;
  memberIdType: MemberIdType;
  personalDataCollected: PersonalDataField[];
  consentChannels: ConsentChannel[];
  dataRetentionMonths: string;
  erasurePolicy: string;
  enabledEventTypes: ProgramEventType[];
  customAttributes: string[];
  webhookUrl: string;
  messagingEnabled: boolean;
  messagingWhatsappNumber: string;
  messagingSmsSenderId: string;
  messagingFromEmail: string;
  quietHoursStart: string;
  quietHoursEnd: string;
  messageFrequencyCap: string;
  offerApprovalThreshold: string;
  adjustmentApprovalThreshold: string;
  supportAgentAdjustmentCap: string;
};

const defaultValues: CreateProgramFormValues = {
  name: "",
  code: "",
  clientName: "",
  billingContactName: "",
  billingContactEmail: "",
  vatNumber: "",
  websiteUrl: "",
  programLogoUri: "",
  industry: "retail",
  programOwner: "",
  brandPrimaryColor: "",
  memberTermsUrl: "",
  memberTermsVersion: "",
  privacyPolicyUrl: "",
  privacyPolicyVersion: "",
  loyaltyPointsName: "",
  costPerPoint: "",
  cashbackPercentage: "",
  pointsRounding: "down",
  pointsHoldDays: "",
  cancellationPeriodDays: "",
  pointsExpiryMode: "months",
  pointsExpiryMonths: "24",
  negativeBalancePolicy: "recover",
  bonusCombineMode: "add",
  paymentCurrency: "AED",
  invoiceFrequency: "30",
  offerDenomination: "1",
  txnCommissionPct: "",
  timeZone: "Asia/Dubai",
  dataRegion: "UAE",
  supportedLanguages: ["en"],
  address: "",
  country: "United Arab Emirates",
  city: "",
  startingTier: "Bronze",
  contacts: [
    {
      name: "",
      email: "",
      role: "Owner",
      countryCode: "+971",
      phoneNumber: "",
    },
  ],
  enrolmentMode: "open",
  memberIdType: "client",
  personalDataCollected: ["name", "email"],
  consentChannels: ["email"],
  dataRetentionMonths: "36",
  erasurePolicy: "Erase personal data within 30 days of a verified request.",
  enabledEventTypes: ["member.enrolled"],
  customAttributes: [],
  webhookUrl: "",
  messagingEnabled: false,
  messagingWhatsappNumber: "",
  messagingSmsSenderId: "",
  messagingFromEmail: "",
  quietHoursStart: "21:00",
  quietHoursEnd: "08:00",
  messageFrequencyCap: "3",
  offerApprovalThreshold: "5000",
  adjustmentApprovalThreshold: "1000",
  supportAgentAdjustmentCap: "500",
};

const CURRENCY_OPTIONS = [
  { value: "INR", label: "INR" },
  { value: "AED", label: "AED" },
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
];

const SECTIONS: {
  id: CreateSection;
  label: string;
  hint: string;
  icon: typeof Building2;
}[] = [
  { id: "identity", label: "Client", hint: "Identity and brand", icon: Building2 },
  { id: "region", label: "Region", hint: "Currency and place", icon: Receipt },
  { id: "economy", label: "Economy", hint: "Points value", icon: Coins },
  { id: "members", label: "Members", hint: "Enrolment and tiers", icon: IdCard },
  { id: "integration", label: "Integration", hint: "Events and messaging", icon: Plug },
  { id: "team", label: "Team", hint: "Contacts and controls", icon: ShieldCheck },
];

const FIELD_SECTION: Record<string, CreateSection> = {
  name: "identity",
  code: "identity",
  clientName: "identity",
  billingContactName: "identity",
  billingContactEmail: "identity",
  vatNumber: "identity",
  websiteUrl: "identity",
  programLogoUri: "identity",
  industry: "identity",
  programOwner: "identity",
  brandPrimaryColor: "identity",
  memberTermsUrl: "identity",
  memberTermsVersion: "identity",
  privacyPolicyUrl: "identity",
  privacyPolicyVersion: "identity",
  loyaltyPointsName: "identity",
  costPerPoint: "economy",
  cashbackPercentage: "economy",
  pointsRounding: "economy",
  pointsHoldDays: "economy",
  cancellationPeriodDays: "economy",
  pointsExpiryMode: "economy",
  pointsExpiryMonths: "economy",
  negativeBalancePolicy: "economy",
  bonusCombineMode: "economy",
  paymentCurrency: "region",
  invoiceFrequency: "region",
  offerDenomination: "region",
  txnCommissionPct: "region",
  timeZone: "region",
  dataRegion: "region",
  supportedLanguages: "region",
  address: "region",
  country: "identity",
  city: "region",
  startingTier: "members",
  contacts: "team",
  enrolmentMode: "members",
  memberIdType: "members",
  personalDataCollected: "members",
  consentChannels: "members",
  dataRetentionMonths: "members",
  erasurePolicy: "members",
  enabledEventTypes: "integration",
  customAttributes: "integration",
  webhookUrl: "integration",
  messagingEnabled: "integration",
  messagingWhatsappNumber: "integration",
  messagingSmsSenderId: "integration",
  messagingFromEmail: "integration",
  quietHoursStart: "integration",
  quietHoursEnd: "integration",
  messageFrequencyCap: "integration",
  offerApprovalThreshold: "team",
  adjustmentApprovalThreshold: "team",
  supportAgentAdjustmentCap: "team",
};

const fieldClass =
  "h-11 rounded-xl border-slate-200 bg-white shadow-none placeholder:text-slate-400";

function sectionStep(id: CreateSection) {
  return SECTIONS.findIndex((section) => section.id === id) + 1;
}

function SectionHeader({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-6">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800/80">
        Step {String(step).padStart(2, "0")}
      </p>
      <h2 className="mt-1.5 text-xl font-semibold tracking-tight text-slate-950">
        {title}
      </h2>
      <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
        {description}
      </p>
    </div>
  );
}

function apiDataRegion(region: DataRegion) {
  return region === "UAE" ? "uae" : "india";
}

function toProgramConfig(data: CreateProgramFormValues): ProgramConfig {
  return {
    industry: data.industry,
    programOwner: data.programOwner.trim(),
    brandPrimaryColor: data.brandPrimaryColor.trim(),
    billingContactName: data.billingContactName.trim(),
    billingContactEmail: data.billingContactEmail.trim(),
    memberTermsUrl: data.memberTermsUrl.trim(),
    memberTermsVersion: data.memberTermsVersion.trim(),
    privacyPolicyUrl: data.privacyPolicyUrl.trim(),
    privacyPolicyVersion: data.privacyPolicyVersion.trim(),
    timeZone: data.timeZone,
    dataRegion: data.dataRegion,
    supportedLanguages: [...data.supportedLanguages],
    pointsRounding: data.pointsRounding,
    pointsHoldDays: parseOptionalWholeNumber(data.pointsHoldDays),
    cancellationPeriodDays: parseCancellationPeriod(data.cancellationPeriodDays),
    pointsExpiryMode: data.pointsExpiryMode,
    pointsExpiryMonths:
      data.pointsExpiryMode === "never"
        ? null
        : parseWholeNumber(data.pointsExpiryMonths),
    negativeBalancePolicy: data.negativeBalancePolicy,
    bonusCombineMode: data.bonusCombineMode,
    economyEffectiveDate: null,
    economyNoticeAcknowledged: false,
    enrolmentMode: data.enrolmentMode,
    memberIdType: data.memberIdType,
    personalDataCollected: [...data.personalDataCollected],
    consentChannels: [...data.consentChannels],
    dataRetentionMonths: parseWholeNumber(data.dataRetentionMonths),
    erasurePolicy: data.erasurePolicy.trim(),
    enabledEventTypes: [...data.enabledEventTypes],
    customAttributes: [...data.customAttributes],
    webhookUrl: data.webhookUrl.trim(),
    messagingEnabled: data.messagingEnabled,
    messagingWhatsappNumber: data.messagingWhatsappNumber.trim(),
    messagingSmsSenderId: data.messagingSmsSenderId.trim(),
    messagingFromEmail: data.messagingFromEmail.trim(),
    quietHoursStart: data.quietHoursStart,
    quietHoursEnd: data.quietHoursEnd,
    messageFrequencyCap: parseWholeNumber(data.messageFrequencyCap),
    offerApprovalThreshold: parseWholeNumber(data.offerApprovalThreshold),
    adjustmentApprovalThreshold: parseWholeNumber(
      data.adjustmentApprovalThreshold,
    ),
    supportAgentAdjustmentCap: parseWholeNumber(
      data.supportAgentAdjustmentCap,
    ),
  };
}

function GeneralCard({
  form,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
                  <SectionHeader
          step={sectionStep("identity")}
          title="Client and identity"
          description="The client company, the program slug, and the brand members see. Terms and privacy can wait until launch."
                  />
                  <div className="grid gap-4 md:grid-cols-2">
                    <FormField
                      control={form.control}
            name="clientName"
            rules={requiredText("Legal name")}
                      render={({ field }) => (
              <FormItem>
                <FormLabel>Legal name*</FormLabel>
                          <FormControl>
                            <Input
                    placeholder="Registered company name"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                <FormDescription className="text-xs">
                  Changeable after launch.
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
            name="country"
            rules={requiredText("Country")}
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Country*</FormLabel>
                <FormControl>
                  <Input className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billingContactName"
            rules={requiredText("Billing contact")}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing contact*</FormLabel>
                            <FormControl>
                              <Input
                    placeholder="Name"
                    className={fieldClass}
                                {...field}
                              />
                            </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="billingContactEmail"
            rules={requiredEmail("Billing email")}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Billing email*</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="billing@company.com"
                    className={fieldClass}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="name"
            rules={{ required: "Program name is required" }}
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Program name*</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. Dummy Retail Rewards"
                    className={fieldClass}
                    {...field}
                  />
                </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
            name="code"
            rules={requiredSlug("Program code")}
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Program code / slug*</FormLabel>
                            <FormControl>
                  <Input
                    placeholder="e.g. westay-rewards"
                    className={fieldClass}
                    {...field}
                  />
                            </FormControl>
                <FormDescription className="text-xs">
                  Used in API paths. Locked after launch.
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
          <EnumSelectField
            control={form.control}
            name="industry"
            label="Industry*"
            options={INDUSTRY_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Industry is required" }}
                    />
                    <FormField
                      control={form.control}
            name="programOwner"
            rules={requiredText("Program owner")}
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Program owner*</FormLabel>
                          <FormControl>
                            <Input
                    placeholder="Admin user"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                <FormDescription className="text-xs">
                  Required. After launch the owner can be transferred, not cleared.
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="vatNumber"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>VAT / Tax ID</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Optional"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="websiteUrl"
                      rules={{ required: "Website URL is required" }}
                      render={({ field }) => (
              <FormItem className="md:col-span-2">
                          <FormLabel>Website URL*</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="https://"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
            name="programLogoUri"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                <FormLabel>Logo URL</FormLabel>
                            <FormControl>
                              <Input
                    placeholder="https:// optional"
                    className={fieldClass}
                                {...field}
                              />
                            </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="brandPrimaryColor"
            rules={optionalHexColor()}
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Brand colour</FormLabel>
                <FormControl>
                  <HexColorInput
                    name={field.name}
                    inputRef={field.ref}
                    value={field.value}
                    onBlur={field.onBlur}
                    onChange={field.onChange}
                    className={fieldClass}
                  />
                </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
            name="loyaltyPointsName"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                <FormLabel>Points name</FormLabel>
                          <FormControl>
                  <Input
                    placeholder="e.g. Westay Points"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                <FormDescription className="text-xs">
                  Optional for a draft. This is the loyalty points name members see.
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
          <FormField
            control={form.control}
            name="memberTermsUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Member terms URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://" className={fieldClass} {...field} />
                </FormControl>
                <FormDescription className="text-xs text-amber-800/90">
                  Required before launch, not to save this draft
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
                    <FormField
                      control={form.control}
            name="memberTermsVersion"
                      render={({ field }) => (
              <FormItem>
                <FormLabel>Terms version</FormLabel>
                          <FormControl>
                            <Input
                    placeholder="e.g. 1 or Oct 2026 notice"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                <FormDescription className="text-xs">
                  Stored with the URL. Not a full legal archive.
                </FormDescription>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="privacyPolicyUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Privacy policy URL</FormLabel>
                <FormControl>
                  <Input placeholder="https://" className={fieldClass} {...field} />
                </FormControl>
                <FormDescription className="text-xs text-amber-800/90">
                  Required before launch, not to save this draft
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
          <FormField
            control={form.control}
            name="privacyPolicyVersion"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Privacy version</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g. 1 or Oct 2026 notice"
                    className={fieldClass}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function LoyaltyCard({
  form,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
}) {
  const pointsExpiryMode = form.watch("pointsExpiryMode");
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
        <SectionHeader
          step={sectionStep("economy")}
          title="Points economy"
          description="Point value, rounding, hold, cancellation period, and expiry. After launch, value changes are future-dated."
        />
        <div className="mb-6 rounded-2xl bg-[#F7F4EE] px-4 py-3 text-sm leading-relaxed text-[#10261c]">
          Expiry never applies backwards to points members already hold. After
          launch, a change to point value, rounding, expiry, or how bonuses
          combine needs a future effective date, approval, and a member notice.
        </div>
        <div className="grid gap-4 md:grid-cols-3">
                    <FormField
                      control={form.control}
                      name="costPerPoint"
            rules={{ required: "Point value is required" }}
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Point value (cost per point)*</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.25"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="cashbackPercentage"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Cashback %</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              placeholder="5"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
          <div className="flex items-start gap-3 rounded-2xl bg-[#F4F7F4] p-4">
            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white text-emerald-800 ring-1 ring-emerald-100">
              <Info className="h-3.5 w-3.5" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-emerald-800">
                        Tip
                      </p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                Cost per point drives liability estimates shown on Program
                Overview.
                      </p>
                    </div>
                  </div>
            </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2">
          <EnumSelectField
                      control={form.control}
            name="pointsRounding"
            label="Rounding*"
            options={ROUNDING_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Rounding is required" }}
                    />
                    <FormField
                      control={form.control}
            name="pointsHoldDays"
            rules={optionalWholeNumber("Hold days")}
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Default hold before points are spendable (days)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                    min={0}
                    placeholder="Optional"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                <FormDescription className="text-xs">
                  Optional extra days after the booking. Leave blank to follow checkout only. Booking points still wait until the day after checkout.
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
            name="cancellationPeriodDays"
            rules={optionalWholeNumber("Cancellation period")}
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Cancellation period (days)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                    min={1}
                    placeholder="Optional"
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                <FormDescription className="text-xs">
                  Optional. A cancellation inside this window takes the points back. After the window, the cancellation is refused. Leave blank for no extra window. Points still wait until the day after checkout.
                </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
          <EnumSelectField
            control={form.control}
            name="pointsExpiryMode"
            label="Default points expiry*"
            options={[
              { value: "months", label: "After a number of months" },
              { value: "never", label: "Never" },
            ]}
            triggerClassName={fieldClass}
            rules={{ required: "Choose a points expiry" }}
            hint="Never applies backwards, including after launch."
                    />
                    <FormField
                      control={form.control}
            name="pointsExpiryMonths"
            rules={expiryMonthsRules(() => form.getValues("pointsExpiryMode"))}
                      render={({ field }) => (
              <FormItem className={pointsExpiryMode === "months" ? "" : "hidden"}>
                <FormLabel>Expiry months*</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                    min={1}
                    className={fieldClass}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
          <EnumSelectField
            control={form.control}
            name="negativeBalancePolicy"
            label="Negative balance*"
            options={NEGATIVE_BALANCE_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Choose a negative balance policy" }}
          />
          <EnumSelectField
            control={form.control}
            name="bonusCombineMode"
            label="How bonuses combine*"
            options={BONUS_COMBINE_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Choose how bonuses combine" }}
                    />
                  </div>
                </CardContent>
              </Card>
  );
}

function BillingCard({
  form,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
                  <SectionHeader
          step={sectionStep("region")}
          title="Region and money"
          description="Currency, time zone, and data region lock after launch. Languages can be added later, not removed."
                  />
                  <div className="grid gap-4 md:grid-cols-2">
          <EnumSelectField
            control={form.control}
            name="paymentCurrency"
            label="Currency*"
            options={CURRENCY_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Currency is required" }}
            hint="Locked after launch. AED, INR, and the currencies already offered."
          />
                    <FormField
                      control={form.control}
            name="invoiceFrequency"
            rules={{ required: "Invoice frequency is required" }}
                      render={({ field }) => (
              <FormItem>
                <FormLabel>Invoice frequency (days)*</FormLabel>
                          <FormControl>
                  <Input type="number" className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="offerDenomination"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Offer denomination</FormLabel>
                <FormControl>
                  <Input type="number" className={fieldClass} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
            name="txnCommissionPct"
                      render={({ field }) => (
                        <FormItem>
                <FormLabel>Txn commission %</FormLabel>
                          <FormControl>
                  <Input type="number" className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <EnumSelectField
            control={form.control}
            name="timeZone"
            label="Time zone*"
            options={TIME_ZONE_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Time zone is required" }}
            hint="Locked after launch."
          />
          <EnumSelectField
            control={form.control}
            name="dataRegion"
            label="Data region*"
            options={DATA_REGION_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Data region is required" }}
            hint="UAE or India. Locked after launch."
          />
          <MultiCheckField
            control={form.control}
            name="supportedLanguages"
            label="Supported languages*"
            options={LANGUAGE_OPTIONS}
            rules={atLeastOne("Select at least one language")}
            hint="At least one. After launch, languages can be added and cannot be removed."
            className="md:col-span-2"
          />
          <FormField
            control={form.control}
            name="address"
            rules={{ required: "Address is required" }}
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Address*</FormLabel>
                <FormControl>
                  <Textarea
                    className="min-h-[120px] rounded-xl border-slate-200 bg-white shadow-none"
                    {...field}
                  />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="city"
            rules={requiredText("City")}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>City*</FormLabel>
                          <FormControl>
                  <Input className={fieldClass} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </CardContent>
              </Card>
  );
}

function TiersCard({
  form,
  assignedTiers,
  startingTier,
  toggleTier,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
  assignedTiers: string[];
  startingTier: string;
  toggleTier: (tierName: string) => void;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
        <div className="mb-6">
          <h3 className="text-lg font-semibold tracking-tight text-slate-950">
            Assign tiers
          </h3>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
            Choose which loyalty tiers this program will use, and set the starting tier for new members.
          </p>
        </div>
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#F7F4EE] px-4 py-4">
                    <div>
            <p className="text-sm font-semibold text-slate-950">
                        {assignedTiers.length} tiers selected
                      </p>
            <p className="mt-0.5 text-xs text-slate-500">
                        Starting tier:{" "}
              <span className="font-medium text-slate-800">{startingTier}</span>
                      </p>
                    </div>
                    <FormField
                      control={form.control}
                      name="startingTier"
                      render={({ field }) => (
              <FormItem className="min-w-[200px]">
                <FormLabel className="text-xs text-slate-500">
                            Starting tier
                          </FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                    <SelectTrigger className="h-10 rounded-xl border-slate-200 bg-white">
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {assignedTiers.map((tier) => (
                                <SelectItem key={tier} value={tier}>
                                  {tier}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {PROGRAM_TIER_CHOICES.map((tier) => {
                      const selected = assignedTiers.includes(tier.name);
                      return (
                        <button
                          key={tier.name}
                          type="button"
                          onClick={() => toggleTier(tier.name)}
                          className={cn(
                  "overflow-hidden rounded-2xl border text-left transition-all",
                  selected
                    ? "border-slate-900 bg-white shadow-[0_10px_30px_-18px_rgba(15,23,42,0.45)]"
                    : "border-slate-200 bg-slate-50/70 hover:border-slate-300 hover:bg-white",
                )}
              >
                              <span
                  className={cn("block h-1.5 w-full", tier.swatch)}
                  aria-hidden="true"
                />
                <span className="block p-4">
                  <span className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-950">
                                {tier.name}
                              </span>
                            <span
                              className={cn(
                                "inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px]",
                                selected
                          ? "border-[#1B7A45] bg-[#1B7A45] text-white"
                                  : "border-slate-300 bg-white text-transparent",
                              )}
                            >
                              ✓
                            </span>
                  </span>
                  <span className="mt-3 block text-xs font-medium uppercase tracking-[0.12em] text-slate-400">
                            {tier.threshold}
                  </span>
                  <span className="mt-1 block text-sm text-slate-600">
                            {tier.description}
                  </span>
                          {selected && startingTier === tier.name ? (
                    <span className="mt-3 inline-flex rounded-full bg-[#F7F4EE] px-2 py-0.5 text-[11px] font-semibold text-amber-900">
                              Starting tier
                            </span>
                          ) : null}
                </span>
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
  );
}

function ContactsCard({
  form,
  fields,
  append,
  remove,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
  fields: { id: string }[];
  append: (value: ContactValues) => void;
  remove: (index: number) => void;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
        <SectionHeader
          step={sectionStep("team")}
          title="Team and controls"
          description="Keep this contact list. Launch needs an owner and an admin. Approval limits sit on the program, not on one person."
        />
        <div className="mb-6 rounded-2xl bg-[#F7F4EE] px-4 py-3 text-sm leading-relaxed text-[#10261c]">
          Saving this draft does not require member terms, a privacy policy, or
          team roles. Leaving draft requires a terms URL, a privacy policy URL,
          a program owner, and at least one Admin contact alongside the Owner.
        </div>
        <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                      <div>
            <h3 className="text-lg font-semibold tracking-tight text-slate-950">
              Contact list
            </h3>
            <p className="mt-1 max-w-xl text-sm text-slate-500">
              The same team list used everywhere else. Roles are owner, admin,
              marketing, analyst, and support. A draft saves without them.
              Launch needs an Owner and at least one Admin.
            </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
            className="h-9 rounded-full border-slate-200 bg-white"
                      onClick={() =>
                        append({
                          name: "",
                          email: "",
                role: "Admin",
                          countryCode: "+91",
                          phoneNumber: "",
                        })
                      }
                    >
            <Plus className="h-4 w-4" /> Add contact
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {fields.map((field, index) => (
            <div key={field.id} className="rounded-2xl bg-[#F7F8F7] p-4 sm:p-5">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-semibold text-slate-800">
                            Contact {index + 1}
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                  className="h-8 rounded-full text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                            disabled={fields.length === 1}
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <FormField
                            control={form.control}
                            name={`contacts.${index}.name`}
                  render={({ field: contactField }) => (
                              <FormItem>
                                <FormLabel>Name</FormLabel>
                                <FormControl>
                        <Input className={fieldClass} {...contactField} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`contacts.${index}.email`}
                  render={({ field: contactField }) => (
                              <FormItem>
                                <FormLabel>Email</FormLabel>
                                <FormControl>
                        <Input className={fieldClass} {...contactField} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`contacts.${index}.role`}
                  render={({ field: contactField }) => (
                              <FormItem>
                                <FormLabel>Role</FormLabel>
                      <Select
                        value={contactField.value}
                        onValueChange={contactField.onChange}
                      >
                                <FormControl>
                          <SelectTrigger className={fieldClass}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                                </FormControl>
                        <SelectContent>
                          {contactRoleOptions(contactField.value).map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name={`contacts.${index}.phoneNumber`}
                  render={({ field: contactField }) => (
                              <FormItem>
                                <FormLabel>Phone</FormLabel>
                                <FormControl>
                        <Input className={fieldClass} {...contactField} />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
  );
}

function MembersCard({
  form,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
        <SectionHeader
          step={sectionStep("members")}
          title="Members"
          description="How people join, which personal data you collect, and how long you keep it. Member ID type locks after launch. Personal data fields can only be added later."
        />
        <div className="grid gap-4 md:grid-cols-2">
          <EnumSelectField
            control={form.control}
            name="enrolmentMode"
            label="Enrolment mode*"
            options={ENROLMENT_MODE_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Enrolment mode is required" }}
          />
          <EnumSelectField
            control={form.control}
            name="memberIdType"
            label="Member ID type*"
            options={MEMBER_ID_TYPE_OPTIONS}
            triggerClassName={fieldClass}
            rules={{ required: "Member ID type is required" }}
            hint="Locked after launch."
          />
          <MultiCheckField
            control={form.control}
            name="personalDataCollected"
            label="Personal data collected*"
            options={PERSONAL_DATA_OPTIONS}
            rules={atLeastOne("Select at least one personal data field")}
            hint="At least one. After launch, fields can be added and cannot be removed."
            className="md:col-span-2"
          />
          <MultiCheckField
            control={form.control}
            name="consentChannels"
            label="Consent capture*"
            options={CONSENT_CHANNEL_OPTIONS}
            rules={atLeastOne("Select at least one consent channel")}
            className="md:col-span-2"
          />
          <FormField
            control={form.control}
            name="dataRetentionMonths"
            rules={requiredWholeNumber("Data retention months", 1)}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Keep data for (months)*</FormLabel>
                <FormControl>
                  <Input type="number" min={1} className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="erasurePolicy"
            rules={requiredText("Erasure policy")}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Erasure policy*</FormLabel>
                <FormControl>
                  <Input
                    placeholder="How a deletion request is handled"
                    className={fieldClass}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
            </div>
      </CardContent>
    </Card>
  );
}

function IntegrationCard({
  form,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
        <SectionHeader
          step={sectionStep("integration")}
          title="Integration"
          description="Events this program accepts, where webhooks go, and how messages are sent. Event types and custom attributes can only be added after launch."
        />
        <div className="grid gap-4 md:grid-cols-2">
          <MultiCheckField
            control={form.control}
            name="enabledEventTypes"
            label="Event types*"
            options={EVENT_TYPE_OPTIONS}
            rules={atLeastOne("Enable at least one event type")}
            hint="At least one. After launch, event types can be added and cannot be removed."
            className="md:col-span-2"
          />
          <CustomAttributesField
            control={form.control}
            name="customAttributes"
            inputClassName={fieldClass}
            className="md:col-span-2"
          />
          <FormField
            control={form.control}
            name="webhookUrl"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Webhook URL</FormLabel>
                <FormControl>
                  <Input placeholder="https:// optional" className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="messagingEnabled"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-[#F7F4EE] px-4 py-3">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(next) => field.onChange(next === true)}
                  />
                  <span>
                    <span className="block text-sm font-medium text-slate-900">
                      Campaigns and messaging are used
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-slate-600">
                      Sender, quiet hours, and frequency are set on each campaign.
                    </span>
                  </span>
                </label>
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function ControlsCard({
  form,
}: {
  form: UseFormReturn<CreateProgramFormValues>;
}) {
  return (
    <Card className="rounded-[24px] border-slate-200/80 bg-white shadow-none">
      <CardContent className="p-6 sm:p-8">
        <div className="mb-6 mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-lg font-semibold tracking-tight text-slate-950">
            Approval controls
          </h3>
          <p className="mt-1 max-w-xl text-sm leading-relaxed text-slate-500">
            Thresholds for offers and adjustments, plus the support agent cap.
            Defaults are filled in and stay changeable after launch.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="offerApprovalThreshold"
            rules={requiredWholeNumber("Offer approval threshold", 0)}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Offer approval threshold (points)*</FormLabel>
                <FormControl>
                  <Input type="number" min={0} className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="adjustmentApprovalThreshold"
            rules={requiredWholeNumber("Adjustment approval threshold", 0)}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adjustment approval threshold (points)*</FormLabel>
                <FormControl>
                  <Input type="number" min={0} className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="supportAgentAdjustmentCap"
            rules={requiredWholeNumber("Support agent adjustment cap", 0)}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Support agent point-adjustment cap*</FormLabel>
                <FormControl>
                  <Input type="number" min={0} className={fieldClass} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CreatePerkProgram() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState<CreateSection>("identity");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const [assignedTiers, setAssignedTiers] = useState<string[]>([
    "Bronze",
    "Silver",
    "Gold",
    "Platinum",
  ]);

  const identityRef = useRef<HTMLDivElement>(null);
  const regionRef = useRef<HTMLDivElement>(null);
  const economyRef = useRef<HTMLDivElement>(null);
  const membersRef = useRef<HTMLDivElement>(null);
  const integrationRef = useRef<HTMLDivElement>(null);
  const teamRef = useRef<HTMLDivElement>(null);

  const sectionRefs: Record<CreateSection, RefObject<HTMLDivElement | null>> = {
    identity: identityRef,
    region: regionRef,
    economy: economyRef,
    members: membersRef,
    integration: integrationRef,
    team: teamRef,
  };

  const form = useForm<CreateProgramFormValues>({
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "contacts",
  });

  const startingTier = form.watch("startingTier");

  useEffect(() => {
    const elements = SECTIONS.map(
      (section) => sectionRefs[section.id].current,
    ).filter((node): node is HTMLDivElement => node !== null);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const id = visible?.target.getAttribute("data-section") as
          | CreateSection
          | null;
        if (id) setActiveSection(id);
      },
      { rootMargin: "-18% 0px -62% 0px", threshold: 0.05 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
    // Section refs are stable; observe once after the first paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToSection = (id: CreateSection) => {
    setActiveSection(id);
    sectionRefs[id].current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const toggleTier = (tierName: string) => {
    setAssignedTiers((prev) => {
      if (prev.includes(tierName)) {
        if (prev.length === 1) {
          toast.error("Keep at least one tier assigned");
          return prev;
        }
        const next = prev.filter((tier) => tier !== tierName);
        if (form.getValues("startingTier") === tierName) {
          form.setValue("startingTier", next[0]);
        }
        return next;
      }
      return [...prev, tierName];
    });
  };

  const onInvalid = (errors: FieldErrors<CreateProgramFormValues>) => {
    const section = SECTIONS.find((item) =>
      Object.keys(errors).some((key) => FIELD_SECTION[key] === item.id),
    );
    if (section) scrollToSection(section.id);
  };

  const onSubmit = async (data: CreateProgramFormValues) => {
    if (assignedTiers.length === 0) {
      scrollToSection("members");
      toast.error("Assign at least one tier to this program");
      return;
    }
    if (!assignedTiers.includes(data.startingTier)) {
      scrollToSection("members");
      toast.error("Starting tier must be one of the assigned tiers");
      return;
    }

    const activeContacts = data.contacts.filter(
      (contact) => contact.name.trim() && contact.email.trim(),
    );

    const programName = data.name.trim();
    const programCode = data.code.trim();
    const pointName = data.loyaltyPointsName.trim();
    const costPerPoint = Number(data.costPerPoint) || 0;
    const config = toProgramConfig(data);
    const logoUri = data.programLogoUri.trim();

    const payload = {
      name: programName,
      slug: programCode,
      currency: data.paymentCurrency,
      time_zone: config.timeZone,
      data_region: apiDataRegion(config.dataRegion),
      point_value: costPerPoint,
      point_name: pointName,
      program_name: programName,
      program_code: programCode,
      domain: programCode,
      cost_per_point: costPerPoint,
      loyalty_currency_id: pointName,
      invoice_frequency_in_days: Number(data.invoiceFrequency) || 0,
      percentage_of_cashback_to_be_accrued:
        Number(data.cashbackPercentage) || 0,
      tax_id: data.vatNumber.trim(),
      address: data.address.trim(),
      website_url: data.websiteUrl.trim(),
      country: data.country.trim(),
      city: data.city.trim(),
      offer_denomination: Number(data.offerDenomination) || 0,
      txn_commission_pct_charged_to_prg: Number(data.txnCommissionPct) || 0,
      industry: config.industry,
      program_owner: config.programOwner,
      brand_primary_color: config.brandPrimaryColor,
      client_legal_name: data.clientName.trim(),
      billing_contact_name: config.billingContactName,
      billing_contact_email: config.billingContactEmail,
      member_terms_url: config.memberTermsUrl,
      member_terms_version: config.memberTermsVersion,
      privacy_policy_url: config.privacyPolicyUrl,
      privacy_policy_version: config.privacyPolicyVersion,
      program_logo_uri: logoUri,
      supported_languages: config.supportedLanguages,
      points_rounding: config.pointsRounding,
      points_hold_days: config.pointsHoldDays,
      cancellation_period_days: config.cancellationPeriodDays,
      points_expiry_mode: config.pointsExpiryMode,
      points_expiry_months: config.pointsExpiryMonths,
      negative_balance_policy: config.negativeBalancePolicy,
      bonus_combine_mode: config.bonusCombineMode,
      enrolment_mode: config.enrolmentMode,
      member_id_type: config.memberIdType,
      personal_data_collected: config.personalDataCollected,
      consent_channels: config.consentChannels,
      data_retention_months: config.dataRetentionMonths,
      erasure_policy: config.erasurePolicy,
      enabled_event_types: config.enabledEventTypes,
      custom_attributes: config.customAttributes,
      webhook_url: config.webhookUrl,
      messaging_enabled: config.messagingEnabled,
      messaging_whatsapp_number: config.messagingWhatsappNumber,
      messaging_sms_sender_id: config.messagingSmsSenderId,
      messaging_from_email: config.messagingFromEmail,
      quiet_hours_start: config.quietHoursStart,
      quiet_hours_end: config.quietHoursEnd,
      message_frequency_cap: config.messageFrequencyCap,
      offer_approval_threshold: config.offerApprovalThreshold,
      adjustment_approval_threshold: config.adjustmentApprovalThreshold,
      support_agent_adjustment_cap: config.supportAgentAdjustmentCap,
      economy_effective_date: config.economyEffectiveDate,
      economy_notice_acknowledged: config.economyNoticeAcknowledged,
    };

    setIsSubmitting(true);
    try {
      const created = await ProgramsApi.create({
        ...payload,
        assigned_tiers: [...assignedTiers],
        starting_tier: data.startingTier,
        contacts: activeContacts.map((contact) => ({
          name: contact.name.trim(),
          email: contact.email.trim(),
          role: contact.role.trim().toLowerCase(),
          country_code: contact.countryCode,
          phone_number: contact.phoneNumber.trim(),
        })),
      });
      queryClient.invalidateQueries({ queryKey: programKeys.all });
      toast.success("Program created successfully");
      navigate(`/perkpoint/program/setup/${created.id}`);
    } catch (error) {
      // Put each API field error on its form field and jump to the first one.
      const fieldErrors = apiFieldErrors(error);
      let firstSection: CreateSection | undefined;
      Object.entries(fieldErrors).forEach(([apiField, message]) => {
        const formField = PROGRAM_FIELD_FROM_API[apiField] as
          | keyof CreateProgramFormValues
          | undefined;
        if (!formField || !(formField in defaultValues)) return;
        form.setError(formField, { message });
        firstSection ??= FIELD_SECTION[formField];
      });
      if (firstSection) scrollToSection(firstSection);
      toast.error(apiErrorMessage(error, "Failed to create program"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl overflow-x-hidden pb-10">
      <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0">
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="-ml-2 mb-3 h-8 rounded-full px-2 text-slate-500 hover:text-slate-900"
          >
            <Link to="/perkpoint/program/setup">
              <ArrowLeft className="h-4 w-4" />
              Program setup
            </Link>
          </Button>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-emerald-800/80">
            Perk Point
          </p>
          <h1 className="mt-2 text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Create program
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500 sm:text-base">
            Configure a draft in six steps. Nothing reaches members until the
            program leaves draft.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
            className="h-11 rounded-full border-slate-200 bg-white px-5"
              asChild
            >
              <Link to="/perkpoint/program/setup">Cancel</Link>
            </Button>
            <Button
              type="submit"
            form="create-program-page-form"
              disabled={isSubmitting}
            className="h-11 rounded-full bg-[#1B7A45] px-5 text-white shadow-none hover:bg-[#16653A]"
            >
              {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Create program
          </Button>
        </div>
      </div>

      <div className="mt-6 grid items-start gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
        <nav className="sticky top-0 hidden lg:block" aria-label="Program sections">
          <ol className="space-y-1 border-l border-slate-200">
            {SECTIONS.map((section, index) => {
              const isActive = activeSection === section.id;
              return (
                <li key={section.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "relative flex w-full items-start gap-3 rounded-r-xl px-4 py-2.5 text-left transition-colors",
                      isActive
                        ? "bg-emerald-50/80 text-emerald-950"
                        : "text-slate-500 hover:bg-slate-50 hover:text-slate-900",
                    )}
                  >
                    <span
                      className={cn(
                        "absolute -left-px top-3 h-5 w-0.5 rounded-full",
                        isActive ? "bg-[#1B7A45]" : "bg-transparent",
                      )}
                      aria-hidden="true"
                    />
                    <span className="w-5 shrink-0 text-xs font-semibold tabular-nums">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">
                        {section.label}
                      </span>
                      <span className="mt-0.5 block text-xs font-normal text-slate-400">
                        {section.hint}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <div className="min-w-0">
          <div className="mb-5 lg:hidden">
            <div className="flex flex-wrap gap-2">
              {SECTIONS.map((section, index) => {
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    className={cn(
                      "rounded-full px-3 py-1.5 text-sm font-medium",
                      isActive
                        ? "bg-[#10261c] text-white"
                        : "bg-white text-slate-600 ring-1 ring-slate-200",
                    )}
                  >
                    {index + 1}. {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          <Form {...form}>
            <form
              id="create-program-page-form"
              onSubmit={form.handleSubmit(onSubmit, onInvalid)}
              className="space-y-5"
            >
              <div ref={sectionRefs.identity} data-section="identity" className="scroll-mt-28">
                <GeneralCard form={form} />
              </div>
              <div ref={sectionRefs.region} data-section="region" className="scroll-mt-28">
                <BillingCard form={form} />
              </div>
              <div ref={sectionRefs.economy} data-section="economy" className="scroll-mt-28">
                <LoyaltyCard form={form} />
              </div>
              <div ref={sectionRefs.members} data-section="members" className="scroll-mt-28 space-y-5">
                <MembersCard form={form} />
                <TiersCard
                  form={form}
                  assignedTiers={assignedTiers}
                  startingTier={startingTier}
                  toggleTier={toggleTier}
                />
              </div>
              <div
                ref={sectionRefs.integration}
                data-section="integration"
                className="scroll-mt-28"
              >
                <IntegrationCard form={form} />
              </div>
              <div ref={sectionRefs.team} data-section="team" className="scroll-mt-28 space-y-5">
                <ContactsCard
                  form={form}
                  fields={fields}
                  append={append}
                  remove={remove}
                />
                <ControlsCard form={form} />
              </div>

              <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-5">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 rounded-full border-slate-200 bg-white px-5"
                  asChild
                >
                  <Link to="/perkpoint/program/setup">Cancel</Link>
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="h-11 rounded-full bg-[#1B7A45] px-5 text-white shadow-none hover:bg-[#16653A]"
                >
                  {isSubmitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
              )}
              Create program
            </Button>
          </div>
        </form>
      </Form>
        </div>
      </div>
    </div>
  );
}
